/**
 * src/admin-ui/src/pages/ApiKeys.tsx
 *
 * API-key management page on the shared full-screen table pattern
 * (components/edge-table): full data set is loaded at once, so every
 * column gets an Excel-like header filter; the "create key" action
 * lives in the bottom bar.
 */

import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { ApiKey, createApiKey, getApiKeys } from "../services/apiKeys";
import EdgeDataTable, {
  EdgeColumn,
} from "../components/edge-table/EdgeDataTable";
import { BarTile } from "../components/edge-table/EdgeTableLayout.styles";
import { FilterableCell } from "../utils/usageTableFilters";
import { KeyValue } from "./ApiKeys.styles";

const PAGE_SIZE = 100;

const formatCreatedAt = (seconds: number): string =>
  new Date(seconds * 1000).toLocaleString();

const columns: EdgeColumn<ApiKey>[] = [
  { id: "id", label: "ID", filterKind: "number", numeric: true },
  {
    id: "key",
    label: "Ключ",
    filterKind: "text",
    render: (row) => <KeyValue>{row.key}</KeyValue>,
  },
  { id: "created_at", label: "Дата создания", filterKind: "text" },
];

const getCell = (row: ApiKey, columnId: string): FilterableCell => {
  switch (columnId) {
    case "id":
      return { num: row.id, text: String(row.id) };
    case "key":
      return { text: row.key };
    case "created_at":
      return { text: formatCreatedAt(row.created_at) };
    default:
      return { text: "—" };
  }
};

const ApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const data = await getApiKeys();
      setKeys(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch API keys", error);
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createApiKey();
      await fetchKeys();
    } catch (error) {
      console.error("Failed to create API key", error);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  return (
    <EdgeDataTable
      ariaLabel="Список API ключей"
      columns={columns}
      rows={keys}
      getCell={getCell}
      rowKey={(row) => row.id}
      loading={loading}
      emptyMessage="API ключей пока нет"
      clientPageSize={PAGE_SIZE}
      renderMobileCard={(key) => (
        <>
          <Typography variant="caption" color="text.secondary">
            ID {key.id}
          </Typography>
          <KeyValue>{key.key}</KeyValue>
          <Typography variant="body2" color="text.secondary">
            {formatCreatedAt(key.created_at)}
          </Typography>
        </>
      )}
      bottomTiles={
        <BarTile>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? "Создание..." : "Создать новый ключ"}
          </Button>
        </BarTile>
      }
    />
  );
};

export default ApiKeys;
